import { useSelector } from 'react-redux';
import { timestampToDate } from 'Utils/Format';
import { isFreeTrial } from 'Utils/Plans';
import { isFirstResponderUpgradePlan, isMilitaryUpgradePlan, isSheerIdFreePlan } from 'Utils/SheerIdHelpers';
import { isUpgradeSubscription } from 'Api/EvergentApi';

export function usePaymentReview() {
  const promoCode = useSelector((s) => s.promoCode);
  const subscription = useSelector((s) => s.subscription);
  const { selectedPlan, selectedPlanPrice, showDefaultPromo, showFreeTrialPromo } = useSelector((s) => s.plans);
  const { totalTax, isTaxError } = useSelector((s) => s.checkout);
  const { isPlanPayment, showPlanDescOnPaymentReview } = useSelector((s) => s.purchaseMode);
  let shouldShowPromoBox = useSelector((s) => s.appConfig.defaultConfig.showPromoBox);
  const routes = useSelector((s) => s.appConfig.routes);

  const { hasSubscription, isNewUser, isTrialistUser, isChurnedUser, validityTill } = subscription;

  let showPackagePrice = true;
  let defaultPromoName;
  let secondaryLayout = false;
  const isFreeSheerId = isSheerIdFreePlan(selectedPlan?.packageID);
  const isMilitaryUpgrade = isMilitaryUpgradePlan(selectedPlan?.packageID);
  const isFirstResponderUpgrade = isFirstResponderUpgradePlan(selectedPlan?.packageID);
  const isNonTrialistUpgrade = !isTrialistUser && hasSubscription && isUpgradeSubscription(subscription, selectedPlan);

  const planName = selectedPlan?.planName || '';
  let planDescription = selectedPlan?.planDescription || '';
  const priceWithPromoCode = promoCode.priceToBeCharged > 0 ? promoCode.priceToBeCharged : 0;
  const packagePrice = selectedPlanPrice;
  const totalPackagePrice = priceWithPromoCode || selectedPlanPrice;
  let totalPrice = (totalPackagePrice + totalTax).toFixed(2) || (0).toFixed(2);

  //TODO: CLEAN UP LOGIC FOR TOTAL DUE

  // always show "Total due today" unless:
  //    1. plan is a free trial where user isn't immediately billed, or
  //    2. total price is less than 1 cent (except if military discount is being applied)
  //    3. user is downgrading plans
  let defaultTotalLabel = 'Total due today';
  if (isFreeTrial(selectedPlan) || (!totalPrice && !isMilitaryUpgrade && !isFirstResponderUpgrade)) {
    // the following two blocks is preservation of old logic
    if (validityTill && !isMilitaryUpgrade && !isFirstResponderUpgrade) {
      defaultTotalLabel = `Total due on ${timestampToDate(validityTill, 2)}`;
    }
    if (
      selectedPlan?.hasFreePromo &&
      (isNewUser || isChurnedUser) &&
      showDefaultPromo &&
      (showFreeTrialPromo || !!selectedPlan.freePromo.amount)
    ) {
      const { freePromo } = selectedPlan;
      defaultPromoName = freePromo.displayName || '';
      let billDate = freePromo.nextBillingDateTime;
      billDate = timestampToDate(billDate, 2);
      defaultTotalLabel = `Total due on ${billDate}`;
    }
  }
  if (isNonTrialistUpgrade) {
    defaultTotalLabel = 'Total due today';
  }

  const frequencyText = isPlanPayment ? `/${selectedPlan?.planFrequencyText?.replace(' ', '')}` : null;
  const packageClassName = showPlanDescOnPaymentReview ? 'packageReviewItem withPlanDesc' : 'packageReviewItem';
  let changePlanLink = hasSubscription ? routes.changePlans : routes.signupPlans;
  let totalLabel = promoCode.totalLabel || defaultTotalLabel;

  if ((isMilitaryUpgrade || isFirstResponderUpgrade) && !isChurnedUser) {
    changePlanLink = null;
  }

  if (isFreeSheerId) {
    totalPrice = (0).toFixed(2);
    changePlanLink = null;
    totalLabel = 'Total Due';
    shouldShowPromoBox = false;
    showPackagePrice = false;
    defaultPromoName = 'First Year Free';
    planDescription = '';
    secondaryLayout = true;
  }

  return {
    planName,
    planDescription,
    totalLabel,
    totalPrice,
    frequencyText,
    packageClassName,
    isPlanPayment,
    changePlanLink,
    showPackagePrice,
    packagePrice: packagePrice.toFixed(2),
    showPlanDescOnPaymentReview,
    isTaxError,
    shouldShowPromoBox,
    totalTax: totalTax.toFixed(2),
    promoDescription: promoCode.promoDescription || defaultPromoName,
    secondaryLayout,
  };
}
